/**
 * §13.4 "Full gamepad UI navigation" + the global focus ring.
 *
 * Every screen registers its focus order; this controller moves a roving focus
 * through it with D-pad / left stick / arrow keys, repeating at 300 ms first then
 * 110 ms sustained (§3.5). The focus ring itself is CSS (`:focus-visible` plus the
 * forced `.nav-focus` class so gamepad focus is always visible, spine §14).
 *
 * Gamepad polling runs only while a menu screen is open, so it never competes with
 * R9's in-run sampling.
 */

export type NavDirection = 'up' | 'down' | 'left' | 'right';

const REPEAT_FIRST_MS = 300;
const REPEAT_SUSTAIN_MS = 110;
const STICK_ENGAGE = 0.5;
const STICK_RELEASE = 0.35;

/** Standard-mapping indices used for UI navigation only. */
const BTN_CONFIRM = 0;
const BTN_BACK = 1;
const BTN_ALT = 2;
const BTN_START = 9;
const BTN_DPAD_UP = 12;
const BTN_DPAD_DOWN = 13;
const BTN_DPAD_LEFT = 14;
const BTN_DPAD_RIGHT = 15;

export interface NavHost {
  onConfirm(): void;
  onBack(): void;
  onAlt(): void;
  onStart(): void;
}

function isSegmentedChild(node: Element | null): HTMLElement | null {
  if (!node) return null;
  const parent = node.parentElement;
  if (parent && parent.classList.contains('segmented')) return parent;
  return null;
}

export class NavController {
  private stops: HTMLElement[] = [];
  private index = -1;
  private host: NavHost | null = null;
  private polling = false;
  private rafId = 0;
  private nextRepeatAt = 0;
  private heldDir: NavDirection | null = null;
  private prevButtons: boolean[] = [];
  private stickLatched = false;
  private enabled = true;
  private readonly boundTick = (now: number): void => this.tick(now);

  /** Called by every screen after it builds or rebuilds its tree. */
  setStops(stops: readonly HTMLElement[], focusFirst: boolean): void {
    this.stops = stops.slice();
    for (let i = 0; i < this.stops.length; i++) {
      const s = this.stops[i];
      if (!s.hasAttribute('tabindex')) s.tabIndex = 0;
    }
    this.index = this.stops.length > 0 ? 0 : -1;
    if (focusFirst) this.focusIndex(0);
  }

  setHost(host: NavHost | null): void {
    this.host = host;
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (!on) this.stopPolling();
  }

  get current(): HTMLElement | null {
    return this.index >= 0 && this.index < this.stops.length ? this.stops[this.index] : null;
  }

  focusIndex(i: number): void {
    if (this.stops.length === 0) return;
    const n = this.stops.length;
    const next = ((i % n) + n) % n;
    const prev = this.current;
    if (prev) prev.classList.remove('nav-focus');
    this.index = next;
    const node = this.stops[next];
    node.classList.add('nav-focus');
    try {
      node.focus({ preventScroll: false });
    } catch {
      node.focus();
    }
  }

  /** Focus follows the pointer so mouse and pad never disagree about the ring. */
  syncFromElement(node: Element | null): void {
    if (!node) return;
    for (let i = 0; i < this.stops.length; i++) {
      if (this.stops[i] === node || this.stops[i].contains(node)) {
        if (i !== this.index) this.focusIndex(i);
        return;
      }
    }
  }

  move(dir: NavDirection): void {
    if (!this.enabled || this.stops.length === 0) return;
    const cur = this.current;
    if ((dir === 'left' || dir === 'right') && cur) {
      if (cur instanceof HTMLInputElement && cur.type === 'range') {
        const step = Number(cur.step) || 1;
        const delta = dir === 'right' ? step : -step;
        const next = Math.max(Number(cur.min), Math.min(Number(cur.max), Number(cur.value) + delta));
        if (next !== Number(cur.value)) {
          cur.value = String(next);
          cur.dispatchEvent(new Event('input', { bubbles: true }));
        }
        return;
      }
      const group = isSegmentedChild(cur) ?? (cur.classList.contains('segmented') ? cur : null);
      if (group) {
        const kids = Array.prototype.slice.call(group.children) as HTMLElement[];
        let at = kids.indexOf(cur);
        if (at < 0) at = kids.findIndex((k) => k.classList.contains('is-on'));
        const nextAt = Math.max(0, Math.min(kids.length - 1, at + (dir === 'right' ? 1 : -1)));
        if (nextAt !== at) {
          kids[nextAt].click();
          this.replaceStop(cur, kids[nextAt]);
        }
        return;
      }
      if (cur.dataset.navHorizontal === 'true') {
        const ev = dir === 'right' ? 'nav-next' : 'nav-prev';
        cur.dispatchEvent(new CustomEvent(ev, { bubbles: false }));
        return;
      }
    }
    const delta = dir === 'down' || dir === 'right' ? 1 : -1;
    this.focusIndex(this.index + delta);
  }

  private replaceStop(from: HTMLElement, to: HTMLElement): void {
    const i = this.stops.indexOf(from);
    if (i >= 0) {
      from.classList.remove('nav-focus');
      this.stops[i] = to;
      this.index = i;
      to.classList.add('nav-focus');
      to.focus();
    }
  }

  confirm(): void {
    const cur = this.current;
    if (!cur) return;
    if (cur instanceof HTMLInputElement && cur.type === 'range') return;
    cur.click();
  }

  startPolling(): void {
    if (this.polling || !this.enabled) return;
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return;
    this.polling = true;
    this.rafId = requestAnimationFrame(this.boundTick);
  }

  stopPolling(): void {
    if (!this.polling) return;
    this.polling = false;
    cancelAnimationFrame(this.rafId);
    this.heldDir = null;
  }

  private tick(now: number): void {
    if (!this.polling) return;
    this.pollGamepad(now);
    this.rafId = requestAnimationFrame(this.boundTick);
  }

  private pollGamepad(now: number): void {
    const pads = navigator.getGamepads ? navigator.getGamepads() : null;
    if (!pads) return;
    let pad: Gamepad | null = null;
    for (let i = 0; i < pads.length; i++) {
      const p = pads[i];
      if (p && p.connected) {
        pad = p;
        break;
      }
    }
    if (!pad) {
      this.heldDir = null;
      return;
    }

    let dir: NavDirection | null = null;
    if (pressed(pad, BTN_DPAD_UP)) dir = 'up';
    else if (pressed(pad, BTN_DPAD_DOWN)) dir = 'down';
    else if (pressed(pad, BTN_DPAD_LEFT)) dir = 'left';
    else if (pressed(pad, BTN_DPAD_RIGHT)) dir = 'right';

    if (!dir) {
      const ax = pad.axes.length > 0 ? pad.axes[0] : 0;
      const ay = pad.axes.length > 1 ? pad.axes[1] : 0;
      const mag = Math.max(Math.abs(ax), Math.abs(ay));
      if (mag >= STICK_ENGAGE) {
        if (!this.stickLatched || this.heldDir) {
          dir = Math.abs(ay) >= Math.abs(ax) ? (ay < 0 ? 'up' : 'down') : ax < 0 ? 'left' : 'right';
        }
        this.stickLatched = true;
      } else if (mag < STICK_RELEASE) {
        this.stickLatched = false;
      }
    }

    if (dir) {
      if (this.heldDir !== dir) {
        this.heldDir = dir;
        this.move(dir);
        this.nextRepeatAt = now + REPEAT_FIRST_MS;
      } else if (now >= this.nextRepeatAt) {
        this.move(dir);
        this.nextRepeatAt = now + REPEAT_SUSTAIN_MS;
      }
    } else {
      this.heldDir = null;
    }

    this.edge(pad, BTN_CONFIRM, () => {
      this.confirm();
      this.host?.onConfirm();
    });
    this.edge(pad, BTN_BACK, () => this.host?.onBack());
    this.edge(pad, BTN_ALT, () => this.host?.onAlt());
    this.edge(pad, BTN_START, () => this.host?.onStart());
  }

  private edge(pad: Gamepad, index: number, fn: () => void): void {
    const now = pressed(pad, index);
    const before = this.prevButtons[index] === true;
    this.prevButtons[index] = now;
    if (now && !before) fn();
  }

  /** Keyboard nav mirrors the pad so both paths reach every screen. */
  handleKey(e: KeyboardEvent): boolean {
    if (!this.enabled) return false;
    switch (e.key) {
      case 'ArrowUp':
        this.move('up');
        return true;
      case 'ArrowDown':
        this.move('down');
        return true;
      case 'ArrowLeft':
        this.move('left');
        return true;
      case 'ArrowRight':
        this.move('right');
        return true;
      case 'Enter':
        this.confirm();
        return true;
      default:
        return false;
    }
  }
}

function pressed(pad: Gamepad, index: number): boolean {
  const b = pad.buttons[index];
  return b !== undefined && b.pressed;
}
